import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const ColorShowcase = () => {
  const colors = [
    {
      name: "Ocean Blue",
      class: "bg-oceanBlue",
      hex: "#0B7F8C",
      description: "Deep ocean waters, adventure",
    },
    {
      name: "Coral",
      class: "bg-coral",
      hex: "#FF7F6B",
      description: "Tropical reefs, vibrant energy",
    },
    {
      name: "Palm Green",
      class: "bg-palmGreen",
      hex: "#2D5940",
      description: "Tropical palms, nature",
    },
    {
      name: "Sunset Orange",
      class: "bg-sunset",
      hex: "#FF9F43",
      description: "Golden hour, warmth",
    },
    {
      name: "Terracotta",
      class: "bg-terracotta",
      hex: "#D4654A",
      description: "Mediterranean pottery, earth",
    },
    {
      name: "Sandstone",
      class: "bg-sandstone",
      hex: "#F4E4BC",
      description: "Desert dunes, natural beauty",
    },
    {
      name: "Seafoam",
      class: "bg-seafoam",
      hex: "#A8E6CF",
      description: "Gentle waves, serenity",
    },
    {
      name: "Ivory",
      class: "bg-ivory",
      hex: "#FDF8F0",
      description: "Clean elegance, sophistication",
    },
    {
      name: "Stone",
      class: "bg-stone",
      hex: "#8B7D6B",
      description: "Ancient ruins, timeless",
    },
  ];

  const gradients = [
    {
      name: "Ocean to Palm",
      class: "bg-gradient-to-r from-oceanBlue to-palmGreen",
      description: "Primary brand gradient - sea to shore",
    },
    {
      name: "Sunset to Coral",
      class: "bg-gradient-to-r from-sunset to-coral",
      description: "Warm tropical sunset gradient",
    },
    {
      name: "Terracotta to Sunset",
      class: "bg-gradient-to-r from-terracotta to-sunset",
      description: "Mediterranean warmth gradient",
    },
    {
      name: "Ivory to Sandstone",
      class: "bg-gradient-to-br from-ivory to-sandstone",
      description: "Subtle desert landscape gradient",
    },
    {
      name: "Seafoam to Ocean",
      class: "bg-gradient-to-r from-seafoam to-oceanBlue",
      description: "Gentle ocean depth gradient",
    },
  ];

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-foreground mb-8 text-center">
          Tasting Horizons - Modern Travel Palette
        </h1>
        <p className="text-center text-muted-foreground mb-8 max-w-2xl mx-auto">
          A Mediterranean and tropical-inspired color palette that captures the
          essence of world travel - from deep ocean blues to warm sunset
          oranges.
        </p>

        {/* Individual Colors */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Color Palette</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {colors.map((color, index) => (
                <div key={index} className="text-center">
                  <div
                    className={`w-24 h-24 rounded-lg ${color.class} mx-auto mb-3 shadow-lg`}
                  ></div>
                  <h3 className="font-semibold text-foreground">
                    {color.name}
                  </h3>
                  <p className="text-sm text-muted-foreground font-mono">
                    {color.hex}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {color.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gradients */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Gradient Combinations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {gradients.map((gradient, index) => (
                <div key={index} className="text-center">
                  <div
                    className={`w-full h-24 rounded-lg ${gradient.class} mb-3 shadow-lg flex items-center justify-center`}
                  >
                    <span className="text-white font-semibold">
                      {gradient.name}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {gradient.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Usage Examples */}
        <Card>
          <CardHeader>
            <CardTitle>Usage Examples</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Buttons */}
            <div>
              <h4 className="font-semibold mb-3">Buttons</h4>
              <div className="flex flex-wrap gap-3">
                <button className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors">
                  Primary Button
                </button>
                <button className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors">
                  Secondary Button
                </button>
                <button className="px-4 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors">
                  Accent Button
                </button>
                <button className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90 transition-colors">
                  Destructive Button
                </button>
              </div>
            </div>

            {/* Cards */}
            <div>
              <h4 className="font-semibold mb-3">Cards & Backgrounds</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-card border border-border rounded-lg">
                  <h5 className="font-medium text-card-foreground">
                    Default Card
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    Using card background
                  </p>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <h5 className="font-medium text-muted-foreground">
                    Muted Background
                  </h5>
                  <p className="text-sm text-muted-foreground">
                    Subtle background color
                  </p>
                </div>
                <div className="p-4 bg-gradient-to-r from-oceanBlue to-palmGreen text-white rounded-lg">
                  <h5 className="font-medium">Gradient Card</h5>
                  <p className="text-sm text-white/80">With brand gradient</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ColorShowcase;
